
























































































































































































import axios from 'axios';
import CoButton from '@/components/Atoms/co-button/CoButton.vue';
import CoCheckbox from '@/components/Atoms/co-checkbox/CoCheckbox.vue';
import CoText from '@/components/Atoms/co-text/CoText.vue';
import CoInput from '@/components/Molecules/co-input/CoInput.vue';
import CoProgressBar from '@/components/Molecules/co-progress-bar/CoProgressBar.vue';
import CoHeadline from '@/components/Atoms/co-headline/CoHeadline.vue';
import CoRoundButton from '@/components/Atoms/co-round-button/CoRoundButton.vue';
import CoFormGroup from '@/components/Molecules/co-form-group/CoFormGroup.vue';
import CoCard from '@/components/Molecules/co-card/CoCard.vue';
import CoSwitch from '@/components/Atoms/co-switch/CoSwitch.vue';
// lodash helpers
import { cloneDeep, uniq, isEqual } from 'lodash';

// uuid
import { v4 as uuidv4 } from 'uuid';
import CoPeople from '@/components/Molecules/co-people/CoPeople.vue';
import { User } from '@/types/GlobalTypes';
import { th } from 'date-fns/locale';

interface PollOption {
    ID?: string; // backend id
    LocalID?: string; // local temp id
    Text: string;
    Votes?: number; // legacy or internal
    VoteCount?: number; // proto
    VoterIDsToDisplay?: string[]; // proto
    VotersToDisplay?: any[]; // loaded user objects for display
    CurrentUserVoted?: boolean; // proto
}

interface Poll {
    ID?: string;
    PollQuestion: string;
    PollOptions: PollOption[];
    PollMultiSelect?: boolean;
    PollExpiresAt?: number | null; // unix seconds
}

export default {
    name: 'CoPoll',
    components: {
        CoButton,
        CoCheckbox,
        CoText,
        CoInput,
        CoHeadline,
        CoProgressBar,
        CoSwitch,
        CoRoundButton,
        CoFormGroup,
        CoCard,
        CoPeople,
    },
    props: {
        poll: {
            type: Object as () => Poll,
            default: () => ({ PollQuestion: '', PollOptions: [], PollMultiSelect: false }),
        },
        postID: {
            type: String,
            default: '',
        },
        creatorMode: {
            // if true, shows edit button and allows edit/create
            type: Boolean,
            default: false,
        },
        currentUser: {
            type: Object as () => User,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            localPoll: null as Poll | null,
            showResults: false as boolean,
            draft: { PollQuestion: '', PollOptions: [], PollMultiSelect: false } as Poll,
            draggedOptionIndex: null as number | null,
            dragOverOptionIndex: null as number | null,
            dragOverPosition: null as 'before' | 'after' | null,
            voters: [] as User[],
        };
    },
    computed: {
        currentUserID(): string | null {
            return this.currentUser ? this.currentUser.ID : null;
        },
        isDraftValid(): boolean {
            if (!this.draft?.PollQuestion || this.draft.PollQuestion.trim().length === 0) return false;
            const filled = (this.draft?.PollOptions || []).filter((o) => !!o.Text && o.Text.trim().length > 0);
            return filled.length >= 2;
        },
        totalVotes(): number {
            if (!this.localPoll?.PollOptions || this.localPoll.PollOptions.length === 0) return 0;
            return this.localPoll.PollOptions.reduce((sum, opt) => sum + (opt.VoteCount || 0), 0);
        },
    },
    watch: {
        draft: {
            deep: true,
            handler() {
                this.saveDraft();
            },
        },
        poll: {
            deep: true,
            handler(newPoll) {
                if (this.creatorMode) {
                    const hasPrefilledDraft =
                        !!newPoll &&
                        (!!newPoll.PollQuestion ||
                            (Array.isArray(newPoll.PollOptions) && newPoll.PollOptions.length > 0));
                    if (hasPrefilledDraft) {
                        const normalizedDraft = this.normalizeDraft(newPoll);
                        if (!isEqual(normalizedDraft, this.draft)) {
                            this.draft = normalizedDraft;
                        }
                    }
                    return;
                }
                this.syncPoll(newPoll);
            },
        },
    },
    mounted() {
        this.syncPoll(this.poll);
        // Auto-open creation modal when creator and poll is empty
        if (this.creatorMode) {
            this.$nextTick(() => {
                const hasPrefilledDraft =
                    !!this.poll &&
                    (!!this.poll.PollQuestion ||
                        (Array.isArray(this.poll.PollOptions) && this.poll.PollOptions.length > 0));
                if (hasPrefilledDraft) {
                    this.draft = this.normalizeDraft(this.poll);
                    return;
                }
                this.makeDraft();
            });
        }
    },
    methods: {
        syncPoll(poll: Poll): void {
            if (!this.localPoll) {
                this.localPoll = cloneDeep(poll);
                this.getUsersWhoVotedForOption();
                return;
            }

            const existingOptions = new Map<string | undefined, PollOption>(
                this.localPoll.PollOptions.map((option) => [option.ID, option])
            );
            const incomingOptions = poll?.PollOptions || [];
            const reconciledOptions = incomingOptions.map(
                (incomingOption) => existingOptions.get(incomingOption.ID) || cloneDeep(incomingOption)
            );
            const optionIDsChanged =
                reconciledOptions.length !== this.localPoll.PollOptions.length ||
                reconciledOptions.some((option, index) => option !== this.localPoll?.PollOptions[index]);

            if (optionIDsChanged) {
                // Poll labels are immutable; retain existing option objects while adding/removing IDs in place.
                this.localPoll.PollOptions.splice(0, this.localPoll.PollOptions.length, ...reconciledOptions);
            }

            let votersChanged = false;
            incomingOptions.forEach((incomingOption) => {
                const existingOption = existingOptions.get(incomingOption.ID);
                if (!existingOption) return;

                // Vote updates only change the reactive values rendered by the existing option row.
                if (existingOption.VoteCount !== incomingOption.VoteCount) {
                    this.$set(existingOption, 'VoteCount', incomingOption.VoteCount || 0);
                }
                if (existingOption.CurrentUserVoted !== incomingOption.CurrentUserVoted) {
                    this.$set(existingOption, 'CurrentUserVoted', !!incomingOption.CurrentUserVoted);
                }
                if (!isEqual(existingOption.VoterIDsToDisplay || [], incomingOption.VoterIDsToDisplay || [])) {
                    votersChanged = true;
                    this.$set(existingOption, 'VoterIDsToDisplay', cloneDeep(incomingOption.VoterIDsToDisplay || []));
                }
            });

            if (this.localPoll.PollMultiSelect !== poll.PollMultiSelect) {
                this.$set(this.localPoll, 'PollMultiSelect', !!poll.PollMultiSelect);
            }
            if (votersChanged) this.getUsersWhoVotedForOption();
        },
        // UI helpers
        optKey(o: PollOption): string {
            return `${o.ID || o.LocalID || o.Text}`;
        },
        // Creation only
        makeDraft(): void {
            const base: Poll = {
                ID: uuidv4(),
                PollQuestion: '',
                PollMultiSelect: false,
                PollExpiresAt: null,
                PollOptions: [
                    { LocalID: uuidv4(), Text: '' },
                    { LocalID: uuidv4(), Text: '' },
                ],
            };
            this.draft = base;
        },
        normalizeDraft(source: Poll, includeEmptyOptions = true): Poll {
            const normalizedOptions = (source?.PollOptions || [])
                .map((option) => ({
                    ID: option?.ID,
                    LocalID: option?.LocalID || uuidv4(),
                    Text: option?.Text || '',
                }))
                .filter((option) => (includeEmptyOptions ? true : option.Text.trim().length > 0));

            if (includeEmptyOptions) {
                while (normalizedOptions.length < 2) {
                    normalizedOptions.push({
                        ID: '',
                        Text: '',
                        LocalID: uuidv4(),
                    });
                }
            }

            return {
                ID: source?.ID || uuidv4(),
                PollQuestion: source?.PollQuestion || '',
                PollMultiSelect: !!source?.PollMultiSelect,
                PollExpiresAt: source?.PollExpiresAt || null,
                PollOptions: normalizedOptions,
            };
        },

        addOption() {
            this.draft.PollOptions.push({ LocalID: uuidv4(), Text: '' });
        },
        removeOption(idx: number) {
            this.draft.PollOptions.splice(idx, 1);
        },
        optionIdentifier(opt: PollOption): string {
            return (opt?.ID || opt?.LocalID || '') + '';
        },
        onDragStart(event: DragEvent, index: number) {
            this.draggedOptionIndex = index;
            if (event.dataTransfer) {
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', String(index));
            }
        },
        onDragEnd() {
            this.resetDragState();
        },
        onDragOver(event: DragEvent, index: number) {
            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = 'move';
            }
            const target = event.currentTarget as HTMLElement | null;
            if (!target) return;
            const rect = target.getBoundingClientRect();
            const offsetY = event.clientY - rect.top;
            const position = offsetY < rect.height / 2 ? 'before' : 'after';
            this.dragOverOptionIndex = index;
            this.dragOverPosition = position;
        },
        onDragLeave(event: DragEvent, index: number) {
            const currentTarget = event.currentTarget as HTMLElement | null;
            const related = event.relatedTarget as HTMLElement | null;
            if (!currentTarget) return;
            if (!related || !currentTarget.contains(related)) {
                if (this.dragOverOptionIndex === index) {
                    this.dragOverOptionIndex = null;
                    this.dragOverPosition = null;
                }
            }
        },
        onDrop(event: DragEvent, index: number) {
            if (this.draggedOptionIndex === null) {
                this.resetDragState();
                return;
            }
            const fromIndex = this.draggedOptionIndex;
            if (fromIndex < 0 || fromIndex >= this.draft.PollOptions.length) {
                this.resetDragState();
                return;
            }
            let targetIndex = index;

            if (this.dragOverPosition === 'after') {
                targetIndex += 1;
            }

            if (fromIndex < targetIndex) {
                targetIndex -= 1;
            }

            if (fromIndex !== targetIndex && targetIndex >= 0) {
                const moved = this.draft.PollOptions.splice(fromIndex, 1);
                if (moved.length) {
                    this.draft.PollOptions.splice(targetIndex, 0, moved[0]);
                }
            }

            this.resetDragState();
        },
        resetDragState() {
            this.draggedOptionIndex = null;
            this.dragOverOptionIndex = null;
            this.dragOverPosition = null;
        },
        saveDraft() {
            // Persist only meaningful options; blank rows are UI-only and should not be stored as draft data.
            const draftSnapshot = this.normalizeDraft(this.draft, false);
            if (!this.isDraftValid) {
                this.$emit('updated', draftSnapshot);
                return;
            }
            const cleaned = {
                ...draftSnapshot,
                PollQuestion: draftSnapshot.PollQuestion.trim(),
                PollOptions: draftSnapshot.PollOptions.filter((o) => o.Text && o.Text.trim().length > 0).map((o) => ({
                    ID: o.ID,
                    LocalID: o.LocalID,
                    Text: o.Text.trim(),
                })),
            } as Poll;
            this.$emit('updated', cleaned);
        },
        // Voting
        isSelected(opt: PollOption): boolean {
            const id = opt.ID;
            return this.localPoll?.PollOptions.some((o) => o.ID === id && o.CurrentUserVoted);
        },
        toggleSelect(opt: PollOption) {
            if (!this.localPoll) return;
            const id = opt.ID || opt.LocalID;
            const targetOpt = this.localPoll.PollOptions.find((o) => o.ID === id);
            if (!targetOpt) return;

            // add/remove vote
            if (targetOpt.CurrentUserVoted) {
                targetOpt.CurrentUserVoted = false;
                targetOpt.VoteCount = (targetOpt.VoteCount || 1) - 1;
                // if currentUserID is set, remove from VoterIDsToDisplay remove
                if (this.currentUserID && targetOpt.VoterIDsToDisplay) {
                    targetOpt.VoterIDsToDisplay = targetOpt.VoterIDsToDisplay.filter(
                        (uid) => uid !== this.currentUserID
                    );
                    targetOpt.VotersToDisplay = targetOpt.VotersToDisplay?.filter((u) => u.ID !== this.currentUserID);

                    // replace option in localPoll to trigger reactivity
                }
                const votetoremove = {
                    OptionID: targetOpt.ID,
                    UserID: this.currentUserID,
                };
                this.$emit('vote-removed', votetoremove);
            } else {
                // if not multi-select, clear other selections
                const atLeastOneSelected = this.localPoll.PollOptions.some((o) => o.CurrentUserVoted);
                if (atLeastOneSelected && !this.localPoll.PollMultiSelect) {
                    return;
                }
                // New polls omit these fields; add them reactively so the first vote invalidates totalVotes.
                this.$set(targetOpt, 'CurrentUserVoted', true);
                this.$set(targetOpt, 'VoteCount', (targetOpt.VoteCount || 0) + 1);
                // if currentUserID is set, add to VoterIDsToDisplay
                if (this.currentUserID) {
                    targetOpt.VoterIDsToDisplay = targetOpt.VoterIDsToDisplay || [];
                    targetOpt.VoterIDsToDisplay.push(this.currentUserID);
                    targetOpt.VotersToDisplay = targetOpt.VotersToDisplay || [];
                    targetOpt.VotersToDisplay.push(this.currentUser);
                }
                this.getUsersWhoVotedForOption();
                const votetoadd = {
                    OptionID: targetOpt.ID,
                    UserID: this.currentUserID,
                };
                this.$emit('vote-added', votetoadd);
            }
        },
        showAllVoters(opt: PollOption) {
            if (!opt) return;

            this.voters = [];
            this.votersNextPageUrl = null;

            this.listAllVotersForOption(opt);

            (this.$refs.votersmodal as any).show();
        },

        listAllVotersForOption(opt: PollOption) {
            let url = `/post/poll/vote/list?postID=${this.postID}&optionID=${opt.ID}`;
            if (this.votersNextPageUrl) {
                url = this.votersNextPageUrl;
            }
            axios({
                method: 'GET',
                url,
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    if (!response || !response.data) return;
                    let users = response.data?.objects || [];
                    users = users.map((u: User) => ({
                        ...u['object'],
                        ...u['object']['Profile'],
                    }));
                    this.votersNextPageUrl = response.data?.next || null;

                    this.voters = this.voters.concat(users);
                })
                .catch((error) => {
                    console.error('Error fetching users who voted:', error);
                });
        },

        getUsersWhoVotedForOption() {
            let userIDs: string[] = [];
            const loadedUsers = new Map<string, User>();
            this.localPoll?.PollOptions.forEach((opt) => {
                if (opt.VoterIDsToDisplay) {
                    userIDs = userIDs.concat(opt.VoterIDsToDisplay);
                }
                (opt.VotersToDisplay || []).forEach((user) => loadedUsers.set(user.ID, user));
            });

            userIDs = uniq(userIDs);
            const missingUserIDs = userIDs.filter((id) => !loadedUsers.has(id));
            if (missingUserIDs.length === 0) {
                this.syncVotersForDisplay(loadedUsers);
                return;
            }
            axios({
                method: 'POST',
                url: '/user/listbyids',
                data: { IDS: missingUserIDs.map((id) => ({ ID: id })) },
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    if (!response || !response.data) return;
                    const users = response.data?.Users || [];
                    users.forEach((user: User) => {
                        loadedUsers.set(user.ID, { ...user, ...user['Profile'] });
                    });
                    this.syncVotersForDisplay(loadedUsers);
                })
                .catch((error) => {
                    console.error('Error fetching users who voted:', error);
                });
        },
        syncVotersForDisplay(loadedUsers: Map<string, User>) {
            this.localPoll?.PollOptions.forEach((opt) => {
                const voters = (opt.VoterIDsToDisplay || []).map((id) => loadedUsers.get(id)).filter(Boolean) as User[];
                const displayedVoters = opt.VotersToDisplay || [];
                if (
                    !isEqual(
                        displayedVoters.map((user) => user.ID),
                        voters.map((user) => user.ID)
                    )
                ) {
                    // Change the existing array so unchanged voter thumbnail components remain mounted.
                    displayedVoters.splice(0, displayedVoters.length, ...voters);
                }
                this.$set(opt, 'VotersToDisplay', displayedVoters);
            });
        },
    },
};
